import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/nl";

import Navbar from "../Navbar/Navbar";
import ScaledCanvas from "../Dashboard/components/ScaledCanvas";
import TopbarClock from "../Dashboard/components/TopbarClock";
import TeamTargetHero from "../Dashboard/components/TeamTargetHero";
import MostSoldServices from "../Dashboard/components/MostSoldServices";
import MonthlyRankList from "../Dashboard/components/MonthlyRankList";
import YearRankList from "../Dashboard/components/YearRankList";
import AnimationLayer from "../Dashboard/animations/AnimationLayer";

import { animations, tourAnimations } from "../Dashboard/animations/registry";
import { alertStyle, alertStyles, setAlertStyle } from "../Dashboard/animations/horn";
import {
  audioReady,
  setSoundOn,
  soundOn,
} from "../Dashboard/audio/audioEngine";
import {
  playCue,
  previewSoundSet,
  setSoundSet,
  soundSet,
  soundSets,
} from "../Dashboard/audio/cues";
import {
  chooseSample,
  chosenFile,
  extraVoices,
  foundSamples,
  sampleOptions,
  setExtraVoices,
  warmSamples,
} from "../Dashboard/audio/samples";
import { useAnimations } from "../Dashboard/hooks/useAnimations";
import { useFrameWatch, useRenderTier } from "../Dashboard/hooks/useRenderTier";
import {
  autoRenderTier,
  forceRenderTier,
  isTierForced,
} from "../Dashboard/utils/renderTier";
import { useRankView } from "../Dashboard/hooks/useRankView";
import { useSecretCode } from "../Dashboard/hooks/useSecretCode";
import { useHeldRanking } from "../Dashboard/hooks/useHeldRanking";
import { useHeldTeam } from "../Dashboard/hooks/useHeldTeam";
import { diffGroups } from "../Dashboard/hooks/useSaleEvents";
import { targetProgress } from "../Dashboard/utils/targetProgress";
import { formatCurrency } from "../Dashboard/utils/format";
import { mockData } from "./mockData";
import { labUnlocked, lockLab } from "./labAccess";
import {
  labAmounts,
  labBests,
  labRecord,
  labScenarios,
  labSellers,
  payments,
  applySale,
  nearTarget,
  overTarget,
  paymentSale,
  raiseRecords,
  scenarioFor,
} from "./labData";

import "../Dashboard/Dashboard.css";
import "./AnimationLab.css";

// Het animatielab: het dashboard met testcijfers en een bedieningspaneel
// buiten de ScaledCanvas. Scenario's boeken echte betalingen, het dashboard
// kiest zelf de animaties via dezelfde vergelijking als live.
// Toetsen: 1..9 betaling, 0 target, P feest, Spatie alles, Esc stop,
// R reset, M geluid, H paneel, "nee" terug (dus geen n/e als sneltoets).

const tabs = [
  ["sales", "Verkoop"],
  ["animations", "Animaties"],
  ["sound", "Geluid"],
  ["display", "Beeld"],
];

// Onthoudt het open tabblad over een herlaad heen; standaard Verkoop.
const tabStoreKey = "lab.tab";
const readTab = () => {
  try {
    const saved = window.localStorage.getItem(tabStoreKey);
    return tabs.some(([id]) => id === saved) ? saved : "sales";
  } catch {
    return "sales";
  }
};
const saveTab = (id) => {
  try {
    window.localStorage.setItem(tabStoreKey, id);
  } catch {
    // Geen opslag beschikbaar.
  }
};

// Animaties per soort; onbekende ids komen onder "Overig".
const animationGroups = [
  ["Verkoop", ["screen-glow", "sale-attention", "sale-charge", "charge-burst", "target-feed", "sale-splash", "row-flash", "confetti-burst", "gauge-pulse"]],
  ["Records", ["record-build", "record-stamp", "personal-best", "fireworks"]],
  ["Ranglijst", ["rank-up", "podium-bounce"]],
  ["Target", ["target-charge", "target-hit", "target-party-dim", "target-song"]],
  ["Na opstarten", ["missed-sales", "missed-nothing"]],
  ["Feest", animations.filter((a) => a.id.startsWith("party-")).map((a) => a.id)],
];
const groupedIds = new Set(animationGroups.flatMap(([, ids]) => ids));
const groupedAnimations = [
  ...animationGroups,
  ["Overig", animations.filter((a) => !groupedIds.has(a.id)).map((a) => a.id)],
]
  .map(([label, ids]) => [label, animations.filter((a) => ids.includes(a.id))])
  .filter(([, list]) => list.length > 0);

// Kwaliteitstrappen, zie Dashboard/utils/renderTier.js.
const tierChoices = [
  ["high", "Hoog"],
  ["mid", "Midden"],
  ["low", "Laag"],
];
const tierName = Object.fromEntries(tierChoices);

// Pauze vlak onder het target voor de verkoop, iets langer dan de tellers lopen.
const nearTargetPause = 2000;

// Sneltoetshint naast een knop.
const Key = ({ children }) => <span className="lab-hint">{children}</span>;

const AnimationLab = () => {
  const { month, year } = useParams();
  const shownMonth = dayjs(`${year}-${month}`).locale("nl");
  const monthName = shownMonth.format("MMMM");
  const previousMonthName = shownMonth.subtract(1, "month").format("MMMM");

  const [data, setData] = useState(mockData);
  const [lastSales, setLastSales] = useState([]);
  const [scenario, setScenario] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [tab, setTab] = useState(readTab);
  // sound is de keuze, ready of de browser het ook doorlaat.
  const [sound, setSound] = useState(soundOn);
  const [ready, setReady] = useState(false);
  const [set, setSet] = useState(soundSet);
  const [alertPick, setAlertPick] = useState(alertStyle);
  const [voices, setVoices] = useState(extraVoices);
  // Eigen geluidsbestanden in public/sounds/.
  const [samples, setSamples] = useState([]);
  // Gekozen bestand per animatie, alleen voor de weergave (bron is localStorage).
  const [picks, setPicks] = useState(() =>
    Object.fromEntries(Object.keys(sampleOptions).map((id) => [id, chosenFile(id)])),
  );

  const chooseTab = useCallback((id) => {
    setTab(id);
    saveTab(id);
  }, []);

  // Terug naar het dashboard via "nee" of de knop.
  const navigate = useNavigate();
  const back = useCallback(() => {
    lockLab();
    navigate(`/dashboard/${month}/${year}`, { replace: true });
  }, [month, navigate, year]);

  useSecretCode({ nee: back });

  // Alleen open via de code; na een refresh is de vlag weg en gaan we terug.
  const allowed = useRef(labUnlocked()).current;
  useEffect(() => {
    if (!allowed) back();
  }, [allowed, back]);

  const rankView = useRankView(data.rankList.length);
  useFrameWatch();
  const { tier } = useRenderTier();
  const tierFixed = isTierForced();
  // Toetsen lopen hier via de eigen handler hieronder.
  const { active, play, playGroup, playParty, stopAll, rootClass } = useAnimations({
    keyboard: false,
  });
  // Ranglijst wisselt pas om als de stijger in beeld komt, net als op de tv.
  const ranking = useHeldRanking(data, active);
  const { settle } = ranking;
  // Meter en balk lopen pas op als het geld in beeld binnenkomt, net als op de tv.
  const team = useHeldTeam(data, active);
  const { settle: settleTeam } = team;

  const timers = useRef([]);
  const later = useCallback((fn, ms) => {
    timers.current.push(setTimeout(fn, ms));
  }, []);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // Ref naast de state, zodat uitgestelde stappen op de laatste stand doorbouwen.
  const dataRef = useRef(mockData);
  const update = useCallback((next) => {
    dataRef.current = next;
    setData(next);
  }, []);

  // De eigen lat van het lab, niet die van de tv.
  const records = useRef({ record: labRecord, bests: labBests });

  // Boekt betalingen als één ophaalronde; het dashboard kiest zelf de animaties.
  const receive = useCallback(
    (sales) => {
      const current = dataRef.current;
      const next = sales.reduce(applySale, current);
      const groups = diffGroups(current, next, records.current.record, records.current.bests);
      records.current = raiseRecords(current, next, records.current);

      update(next);
      setLastSales(sales);
      // Via playGroup, zodat een tweede verkoop op de eerste wacht.
      groups.forEach((group) => playGroup(group));
    },
    [playGroup, update],
  );

  const pay = useCallback(
    (payment) => receive([paymentSale(dataRef.current, payment)]),
    [receive],
  );

  // Eén animatie los, met de bijbehorende cijferwijziging.
  const preview = useCallback(
    (id) => {
      const { data: next, payload, sale } = scenarioFor(id, dataRef.current);
      update(next);
      setLastSales(sale ? [sale] : []);
      play(id, payload);
    },
    [play, update],
  );

  const previewAll = useCallback(() => {
    stopAll();
    let wait = 0;
    tourAnimations.forEach((animation) => {
      later(() => preview(animation.id), wait);
      wait += Math.min(animation.ms, 3000) + 400;
    });
  }, [later, preview, stopAll]);

  // Geluidsset kiezen en meteen laten horen.
  const chooseSet = useCallback((id) => {
    setSoundOn(true);
    setSound(true);
    setSoundSet(id);
    setSet(id);
    previewSoundSet(id);
    setReady(audioReady());
  }, []);

  // Welk seintje er voor een verkoop komt, en meteen laten horen.
  const chooseAlert = useCallback((id) => {
    setSoundOn(true);
    setSound(true);
    setAlertStyle(id);
    setAlertPick(id);
    playCue("screen-glow");
    setReady(audioReady());
  }, []);

  // Geluidsbestand kiezen, zo nodig laden, en meteen laten horen.
  const pickSample = useCallback((id, file) => {
    setSoundOn(true);
    setSound(true);
    setPicks((current) => ({ ...current, [id]: file }));
    chooseSample(id, file).then(() => {
      playCue(id);
      setSamples(foundSamples());
      setReady(audioReady());
    });
  }, []);

  // Alle stemmen: de omroeper bij een nieuwe verkoop, records en het target.
  const toggleVoices = useCallback(() => {
    const next = !extraVoices();
    setExtraVoices(next);
    setVoices(next);
  }, []);

  const toggleSound = useCallback(() => {
    const next = !soundOn();
    setSoundOn(next);
    setSound(next);
    setReady(audioReady());
  }, []);

  // Of de browser geluid doorlaat blijkt pas na afspelen, dus na elke animatie checken.
  useEffect(() => {
    setReady(audioReady());
  }, [active]);

  useEffect(() => {
    let stopped = false;
    Promise.resolve(warmSamples()).then(() => {
      if (!stopped) setSamples(foundSamples());
    });
    return () => {
      stopped = true;
    };
  }, []);

  const reset = useCallback(() => {
    stopAll();
    timers.current.forEach(clearTimeout);
    timers.current = [];
    update(mockData);
    // Ranglijst en meter direct terug, zonder op een stijger of het geld te wachten.
    settle(mockData);
    settleTeam(mockData);
    records.current = { record: labRecord, bests: labBests };
    setLastSales([]);
    setScenario(null);
  }, [settle, settleTeam, stopAll, update]);

  // Scenario vanaf de begincijfers, zodat het elke keer gelijk loopt; feest laat de cijfers staan.
  const runScenario = useCallback(
    (item) => {
      if (item.party) {
        setScenario(item.id);
        return playParty();
      }

      reset();
      setScenario(item.id);
      if (item.animation) return preview(item.animation);
      if (item.overTarget) {
        const next = overTarget(dataRef.current, item.overTarget);
        update(next);
        settleTeam(next);
        return undefined;
      }
      if (!item.nearTarget) return receive(item.sales);

      update(nearTarget(dataRef.current));
      later(() => receive(item.sales), nearTargetPause);
      return undefined;
    },
    [later, playParty, preview, receive, reset, settleTeam, update],
  );

  useEffect(() => {
    const onKey = (event) => {
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === "Escape") return stopAll();
      if (event.key === " ") {
        event.preventDefault();
        return previewAll();
      }

      // Shift+cijfer geeft een leesteken; Shift+8 (feest) vangt useAnimations zelf af.
      const key = event.key.toLowerCase();
      if (key === "h") return setPanelOpen((open) => !open);
      if (key === "r") return reset();
      if (key === "m") return toggleSound();

      const shortcut = labScenarios.find((item) => item.key?.toLowerCase() === key);
      if (shortcut) return runScenario(shortcut);

      const payment = payments.find((item) => item.key === key);
      if (payment) pay(payment);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pay, previewAll, reset, runScenario, stopAll, toggleSound]);

  const progress = targetProgress({
    scored: team.scoredTowardsTarget,
    target: team.teamTarget.amount,
    month,
    year,
  });

  if (!allowed) return null;

  const monthLabel = monthName.toUpperCase();
  const running = new Set(active.map((run) => run.id));
  const changed = Math.max(0, data.amount - mockData.amount);
  const sampleChoices = Object.entries(sampleOptions).filter(
    ([, option]) => option.files.length > 1,
  );

  return (
    <>
      <ScaledCanvas>
        <div className={`dashboard-container ${rootClass}`}>
          <Navbar />
          <TopbarClock />
          <AnimationLayer active={active} />

          <div className="dash-body">
            <TeamTargetHero
              monthLabel={monthLabel}
              teamTarget={team.teamTarget}
              amount={team.amount}
              progress={progress}
            />

            <section className="dash-cols">
              <MostSoldServices
                soldServices={data.soldServices}
                monthLabel={monthLabel}
              />
              <MonthlyRankList
                rankList={ranking.rankList}
                rankView={rankView}
                now={monthName}
                bestSeller={data.bestSeller}
                previousMonth={previousMonthName}
                deals={data.deals}
                existantDeals={data.existantDeals}
              />
              <YearRankList yearRankList={ranking.yearRankList} />
            </section>
          </div>
        </div>
      </ScaledCanvas>

      {/* Binnen het lab mag de paneelknop wel zichtbaar zijn. */}
      {!panelOpen && (
        <button className="lab-tab" onClick={() => setPanelOpen(true)}>
          Lab <Key>H</Key>
        </button>
      )}

      <aside className={`lab-panel${panelOpen ? "" : " is-hidden"}`}>
        <header className="lab-head">
          <div className="lab-title">
            <h1>Animatielab</h1>
            <span>testcijfers</span>
          </div>
          <button className="lab-btn is-quiet" onClick={back} title='Of typ "nee"'>
            Naar dashboard
          </button>
          <button className="lab-btn is-quiet" onClick={() => setPanelOpen(false)}>
            Sluiten <Key>H</Key>
          </button>
        </header>

        <div className="lab-transport">
          <button className="lab-btn" onClick={previewAll}>
            Alles afspelen <Key>Spatie</Key>
          </button>
          <button className="lab-btn" onClick={stopAll}>
            Stop <Key>Esc</Key>
          </button>
          <button className="lab-btn" onClick={reset}>
            Reset <Key>R</Key>
          </button>
        </div>

        <nav className="lab-tabs">
          {tabs.map(([id, label]) => (
            <button
              key={id}
              className={tab === id ? "is-on" : ""}
              onClick={() => chooseTab(id)}
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="lab-body">
          {tab === "sales" && (
            <>
              <div className="lab-status">
                {lastSales.length > 0 ? (
                  <span>
                    {lastSales.map((sale, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && ", "}
                        <b>
                          {sale.name} +€{formatCurrency(sale.amount)}
                        </b>
                      </React.Fragment>
                    ))}
                    {lastSales.length === 1 && ` ${lastSales[0].service}`}
                  </span>
                ) : (
                  <span>Begincijfers</span>
                )}
                <span className="lab-status-total">
                  team +€{formatCurrency(changed)}
                </span>
              </div>

              <section className="lab-section">
                <h2>Scenario's</h2>
                <ul className="lab-list">
                  {labScenarios.map((item) => (
                    <li key={item.id}>
                      <button
                        className={`lab-item${
                          scenario === item.id && active.length > 0 ? " is-on" : ""
                        }`}
                        onClick={() => runScenario(item)}
                      >
                        <span>{item.name}</span>
                        <span className="lab-item-side">
                          <span className="lab-hint">{item.steps}</span>
                          {item.key && <Key>{item.key}</Key>}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="lab-section">
                <h2>Losse betaling</h2>
                <div className="lab-pad">
                  <span />
                  {labAmounts.map((amount) => (
                    <span key={amount} className="lab-pad-amount">
                      €{formatCurrency(amount)}
                    </span>
                  ))}
                  {labSellers.map((name) => (
                    <React.Fragment key={name}>
                      <span className="lab-pad-name">{name}</span>
                      {payments
                        .filter((payment) => payment.name === name)
                        .map((payment) => (
                          <button
                            key={payment.key}
                            className="lab-btn lab-pad-key"
                            onClick={() => pay(payment)}
                          >
                            {payment.key}
                          </button>
                        ))}
                    </React.Fragment>
                  ))}
                </div>
              </section>
            </>
          )}

          {tab === "animations" &&
            groupedAnimations.map(([label, list]) => (
              <section key={label} className="lab-section">
                <h2>{label}</h2>
                <ul className="lab-list">
                  {list.map((animation) => (
                    <li key={animation.id}>
                      <button
                        className={`lab-item${running.has(animation.id) ? " is-on" : ""}`}
                        onClick={() => preview(animation.id)}
                        title={animation.hint}
                      >
                        <span>{animation.name.replace(/^Feest: /, "")}</span>
                        <span className="lab-hint">
                          {(animation.ms / 1000).toFixed(1)}s
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}

          {tab === "sound" && (
            <>
              <section className="lab-section">
                <h2>Geluid</h2>
                <div className="lab-row">
                  <button
                    className={`lab-btn${sound ? " is-on" : ""}`}
                    onClick={toggleSound}
                  >
                    {sound ? "Aan" : "Uit"} <Key>M</Key>
                  </button>
                </div>
                {sound && !ready && (
                  <p className="lab-warn">
                    De browser blokkeert geluid tot je ergens op de pagina klikt.
                  </p>
                )}
              </section>

              <section className="lab-section">
                <h2 title="Wat er klinkt en te zien is vlak voor een verkoop">Seintje</h2>
                <div className="lab-grid">
                  {alertStyles.map((option) => (
                    <button
                      key={option.id}
                      className={`lab-btn${alertPick === option.id ? " is-on" : ""}`}
                      onClick={() => chooseAlert(option.id)}
                      title={option.note}
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              </section>

              <section className="lab-section">
                <h2 title="Uit: geen omroeper bij een verkoop (de Attentie-band blijft), en geen stem bij records en target">
                  Stemmen: omroeper, records en target
                </h2>
                <div className="lab-row">
                  <button
                    className={`lab-btn${voices ? " is-on" : ""}`}
                    onClick={toggleVoices}
                    title="Attentie! We hebben een nieuwe verkoop!, Grootste verkoop ooit, Persoonlijk record en Het teamtarget is gehaald"
                  >
                    {voices ? "Aan" : "Uit"}
                  </button>
                </div>
              </section>

              <section className="lab-section">
                <h2>Set</h2>
                <div className="lab-grid">
                  {soundSets.map((option) => (
                    <button
                      key={option.id}
                      className={`lab-btn${set === option.id ? " is-on" : ""}`}
                      onClick={() => chooseSet(option.id)}
                      title={option.note}
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              </section>

              {sampleChoices.map(([id, option]) => (
                <section key={id} className="lab-section">
                  <h2 title={option.hint}>{option.label}</h2>
                  <div className="lab-grid">
                    {option.files.map((entry) => (
                      <button
                        key={entry.file}
                        className={`lab-btn${picks[id] === entry.file ? " is-on" : ""}`}
                        onClick={() => pickSample(id, entry.file)}
                        title={entry.note}
                      >
                        {entry.name}
                      </button>
                    ))}
                  </div>
                </section>
              ))}

              <p className="lab-note">
                {samples.length > 0 && <>Eigen bestanden: {samples.join(", ")}. </>}
                Op de tv: <code>?sound=off</code> <code>?sound=on</code> <code>?seintje=oud</code>{" "}
                <code>?stemmen=aan</code>
              </p>
            </>
          )}

          {tab === "display" && (
            <>
              <section className="lab-section">
                <h2>Kwaliteit</h2>
                <div className="lab-segment">
                  <button
                    className={tierFixed ? "" : "is-on"}
                    onClick={autoRenderTier}
                  >
                    Auto
                  </button>
                  {tierChoices.map(([id, label]) => (
                    <button
                      key={id}
                      className={tier === id && tierFixed ? "is-on" : ""}
                      onClick={() => forceRenderTier(id)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="lab-note">
                  Nu {(tierName[tier] || tier).toLowerCase()},{" "}
                  {tierFixed ? "vastgezet" : "gemeten"}.
                </p>
              </section>

              <p className="lab-note">
                Op de tv: <code>?fx=low</code> <code>?fx=auto</code>
              </p>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default AnimationLab;
